import React, { useEffect, useState } from "react";
import TransactionModal from "./TransactionModal";
import CreditFilterDropdown from "./CreditFilterDropdown";
import CreditPurchaseModal from "./CreditPurchaseModal";
import {
  fetchAutoPurchase,
  fetchUserCreditBalance,
} from "../../apis/creditService";
import {
  getUserCreditHistory,
  getCreditHistoryDetails,
} from "../../apis/creditService";
import { CreditEntry, CreditEntryResponse, CreditHistoryDetail } from "./types";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import TokensIcon from "../../assets/images/tokens.png";

interface CreditFilterParams {
  fromDate?: string;
  toDate?: string;
  isCreditSpent?: boolean;
  isCreditReceived?: boolean;
}

type SortOrder = string;

export interface CreditBalanceResponse {
  availableCredits: number;
  isAutoPurchase: boolean;
}

const Index = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [sortOrder, setSortOrder] = useState<SortOrder>("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [creditHistory, setCreditHistory] = useState<CreditEntryResponse>({
    items: [],
    totalCount: 0,
    page: currentPage,
    pageSize: pageSize,
  });

  const [creditHistoryData, setCreditHistoryData] = useState<
    CreditEntry[] | null
  >();
  const [creditBalance, setCreditBalance] = useState<CreditBalanceResponse>();
  const [selectedTransaction, setSelectedTransaction] =
    useState<CreditHistoryDetail | null>(null);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState("");

  const creditTypeColors: { [key: string]: string } = {
    Respond: "#58BE74",
    Request: "#F1A560",
    Purchase: "#2892C5",
    Signup: "#90A9FF",
    Referral: "#EF8B87",
  };
  const [isFiltered, setIsFiltered] = useState(false);

  const [filters, setFilters] = useState<CreditFilterParams>({
    fromDate: "",
    toDate: "",
    isCreditSpent: undefined,
    isCreditReceived: undefined,
  });

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(creditHistory?.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const totalPages = Math.ceil(creditHistory?.totalCount / pageSize);
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);
    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - 4);
    }
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const fetchData = async () => {
    showLoader();
    try {
      const startTime = Date.now();
      const balanceResponse = await fetchUserCreditBalance();
      const historyResponse = await getUserCreditHistory({
        page: currentPage,
        size: pageSize,
        sortBy: "CreatedDate",
        sortOrder: "asc",
      });
      let autoPurchaseResult = "false";
      if (balanceResponse.isAutoPurchase) {
        autoPurchaseResult = await fetchAutoPurchase();
      }
      const elapsedTime = Date.now() - startTime;
      const delay = Math.max(500, 1000 - elapsedTime);

      setTimeout(() => {
        setCreditBalance({
          availableCredits: balanceResponse.availableCredits,
          isAutoPurchase: balanceResponse.isAutoPurchase,
        });
        setCreditHistory(historyResponse);
        setCreditHistoryData(historyResponse?.items);
        setLoading(false);
        hideLoader();
      }, delay);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    if (!isFiltered) {
      fetchData();
    } else {
      (async () => {
        try {
          showLoader();
          const filteredHistory = await getUserCreditHistory({
            page: currentPage,
            size: pageSize,
            sortBy: "CreatedDate",
            sortOrder: "asc",
            ...filters,
          });
          setCreditHistory(filteredHistory);
          setCreditHistoryData(filteredHistory?.items);
        } catch (error) {
          console.error("Failed to fetch paginated filtered data:", error);
        } finally {
          hideLoader();
        }
      })();
    }
  }, [currentPage]);

  const handleViewDetails = async (id: number) => {
    try {
      const transactionDetails = await getCreditHistoryDetails(id);
      setSelectedTransaction(transactionDetails);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch credit history details:", error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const handleFilterApply = async (filters: CreditFilterParams) => {
    try {
      showLoader();
      setIsFiltered(true);
      setCurrentPage(1);
      setFilters({
        fromDate: filters.fromDate || "",
        toDate: filters.toDate || "",
        isCreditSpent: filters.isCreditSpent,
        isCreditReceived: filters.isCreditReceived,
      });

      const filteredHistory = await getUserCreditHistory({
        page: 1,
        size: pageSize,
        sortBy: "CreatedDate",
        sortOrder: "asc",
        ...filters,
      });

      setCreditHistory(filteredHistory);
      setCreditHistoryData(filteredHistory?.items);
    } catch (error) {
      console.error("Failed to fetch filtered credit history:", error);
    } finally {
      hideLoader();
    }
  };

  const handleClearAll = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      isCreditSpent: undefined,
      isCreditReceived: undefined,
    });
    setIsFiltered(false);
    setCurrentPage(1);
    fetchData();
  };

  const totalPages = Math.ceil(creditHistory?.totalCount / pageSize);

  return (
    <div className="-m-6">
      {/* Token Balance Card */}
      <div className="px-6 pt-6">
        <div className="bg-gradient-to-br from-[#1F2937] to-[#111827] rounded-2xl p-5 max-w-sm">
          <div className="flex items-center gap-4 mb-3">
            <img src={TokensIcon} alt="Tokens" className="w-12 h-12 object-contain" />
            <div>
              <p className="text-white text-3xl font-bold leading-none">
                {(creditBalance?.availableCredits || 0).toString().padStart(2, "0")}
              </p>
              <p className="text-white/60 text-xs mt-1">Total Tokens Earned</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreditPurchaseModal(true)}
            className="w-full bg-[#FBBF24] text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-[#F59E0B] transition-colors"
          >
            Purchase Tokens
          </button>
        </div>
      </div>

      {/* Tokens History Section */}
      <div className="px-6 mt-6">
        {/* Header row */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <p className="text-[#1F2937] text-lg font-semibold">Tokens History</p>
          <div className="flex items-center gap-3 relative">
            {/* Search */}
            <div className="relative">
              <input
                className="block w-56 py-2.5 pl-9 pr-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1F2937] placeholder-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#FBBF24]"
                placeholder="Search by keyword"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
            >
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="text-sm font-medium text-[#374151]">Filters</span>
            </button>

            {isDropdownOpen && (
              <CreditFilterDropdown
                isOpen={isDropdownOpen}
                onClose={closeDropdown}
                onApplyFilters={handleFilterApply}
                initialFromDate={filters.fromDate}
                initialToDate={filters.toDate}
                initialStatus={
                  filters.isCreditSpent
                    ? "spent"
                    : filters.isCreditReceived
                    ? "received"
                    : ""
                }
              />
            )}
          </div>
        </div>

        {/* Credit History Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 grid-cols-1 gap-4">
          {creditHistoryData && creditHistoryData.length > 0 ? (
            creditHistoryData.map((item: CreditEntry, index: number) => {
              const typeCapitalized =
                item.creditType.charAt(0).toUpperCase() +
                item.creditType.slice(1);
              const bgColor = creditTypeColors[typeCapitalized] || "#9CA3AF";

              return (
                <div
                  key={index}
                  className="bg-white border border-[#F3F4F6] rounded-xl p-4"
                >
                  {/* Top row: badge + date */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: bgColor }}
                      >
                        <span className="text-white text-[9px] font-bold">
                          {item.creditAmount > 0 ? "+" : "-"}
                        </span>
                      </span>
                      <span className="text-xs text-[#6B7280] font-medium">
                        {typeCapitalized}
                      </span>
                    </div>
                    <span className="text-[11px] text-[#9CA3AF]">
                      {new Date(item.createdDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })},{" "}
                      {new Date(item.createdDate).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span>
                  </div>

                  {/* Bottom row: airline info + view details */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: bgColor }}
                      >
                        {item.creditAmount > 0
                          ? `+${item.creditAmount}`
                          : item.creditAmount}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#1F2937]">{typeCapitalized}</p>
                        <p className="text-xs text-[#9CA3AF]">Token {item.creditAmount > 0 ? "Earned" : "Spent"}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleViewDetails(item.id)}
                      className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${
                        item.creditType === "respond" || item.creditType === "request"
                          ? "text-[#374151] border-[#E5E7EB] hover:bg-[#F9FAFB]"
                          : "text-[#D1D5DB] border-[#F3F4F6] cursor-not-allowed"
                      }`}
                      disabled={
                        !(item.creditType === "respond" || item.creditType === "request")
                      }
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Details
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-center text-[#9CA3AF] text-sm col-span-3 py-12">
              No token history available.
            </p>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pb-6">
            <div className="flex items-center gap-2 text-sm text-[#6B7280]">
              <span>Rows per page</span>
              <span className="border border-[#E5E7EB] rounded-md px-2 py-1 text-sm text-[#374151]">{pageSize}</span>
            </div>

            <nav>
              <ul className="flex items-center gap-1.5">
                <li>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center justify-center w-8 h-8 rounded-md text-sm ${
                      currentPage === 1
                        ? "text-[#D1D5DB] cursor-not-allowed"
                        : "text-[#6B7280] hover:bg-[#F3F4F6]"
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                </li>

                {getPageNumbers().map((pageNumber) => (
                  <li key={pageNumber}>
                    <button
                      onClick={() => handlePageChange(pageNumber)}
                      className={`flex items-center justify-center w-8 h-8 rounded-md text-sm font-medium ${
                        currentPage === pageNumber
                          ? "bg-[#FBBF24] text-white"
                          : "text-[#6B7280] hover:bg-[#F3F4F6]"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  </li>
                ))}

                <li>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className={`flex items-center justify-center w-8 h-8 rounded-md text-sm ${
                      currentPage >= totalPages
                        ? "text-[#D1D5DB] cursor-not-allowed"
                        : "text-[#6B7280] hover:bg-[#F3F4F6]"
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreditPurchaseModal
        onPaymentComplete={fetchData}
        isOpen={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
      />

      {selectedTransaction && (
        <TransactionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          transaction={selectedTransaction}
        />
      )}
    </div>
  );
};

export default Index;
